import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Walkthrough } from "./types";
import { formatDuration, getWalkthroughPreviewSrc } from "./utils";

type Props = {
  walkthrough: Walkthrough;
  onClick: (walkthrough: Walkthrough) => void;
  /** Highlights the row that matches the walkthrough currently being viewed. */
  isActive?: boolean;
};

/**
 * Compact horizontal row used in the detail screen's "More Walkthroughs"
 * list: a thumbnail with a duration badge on the left, title + category on
 * the right. Selecting a row swaps the active walkthrough.
 */
const WalkthroughListItem = ({ walkthrough, onClick, isActive }: Props) => {
  const theme: Theme = useTheme();

  return (
    <Box
      sx={styles.row(theme, !!isActive)}
      onClick={() => onClick(walkthrough)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick(walkthrough);
      }}
    >
      <Box sx={styles.thumbWrap(theme)}>
        <Box
          component="video"
          src={getWalkthroughPreviewSrc(walkthrough)}
          poster={walkthrough.thumbnail}
          // See WalkthroughCard: the poster makes preloading the master moot.
          preload={walkthrough.thumbnail ? "none" : "metadata"}
          muted
          playsInline
          sx={styles.thumb}
        />
        <Box sx={styles.durationBadge(theme)}>
          {formatDuration(walkthrough.durationMinutes)}
        </Box>
      </Box>

      <Box sx={styles.info}>
        <Box sx={styles.title(theme)}>{walkthrough.title}</Box>
        <Box sx={styles.category(theme)}>{walkthrough.category}</Box>
      </Box>
    </Box>
  );
};

export default WalkthroughListItem;

const styles = {
  row: (theme: Theme, isActive: boolean) => ({
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    borderRadius: "8px",
    cursor: "pointer",
    backgroundColor: isActive ? theme.colorTokens.P[10] : "transparent",
    transition: "background-color 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      backgroundColor: theme.colorTokens.P[10],
    },
  }),
  thumbWrap: (theme: Theme) => ({
    position: "relative" as const,
    flexShrink: 0,
    width: "219px",
    height: "96px",
    borderRadius: "10px",
    overflow: "hidden",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  thumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    display: "block",
    pointerEvents: "none" as const,
    backgroundColor: "#000",
  },
  durationBadge: (theme: Theme) => ({
    position: "absolute" as const,
    bottom: "8px",
    right: "8px",
    padding: "4px 8px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[80],
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "15px",
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    opacity: 0.8,
    whiteSpace: "nowrap" as const,
  }),
  info: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    minWidth: 0,
    paddingTop: "2px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "24px",
  }),
  category: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
};
